import React, { useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { FiBell } from "react-icons/fi";
import { postRequestAuthenticated } from "../../../services/rest";
import { fetchbusinessCredentials } from "../../../redux/slices/businessSlice";
import { SkeletonList } from "../widgets/Skeleton";

const fmtTime = (d) => {
  if (!d) return "";
  try {
    const date = new Date(d);
    const diff = (Date.now() - date.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return date.toLocaleDateString([], { day: "numeric", month: "short" });
  } catch (e) {
    return "";
  }
};

// Account-level notifications for the business (plan/billing, limits, etc.).
// Talk-to-us messages live in their own Inbox tab, not here.
const NotificationsTab = () => {
  const dispatch = useDispatch();
  const business = useSelector((s) => s.business.business);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // Snapshot lastRead at mount so we can highlight what's new in THIS view,
  // before we stamp it read below.
  const [lastReadAt] = useState(() =>
    business?.lastReadNotification ? new Date(business.lastReadNotification) : null
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await postRequestAuthenticated("/fetch/business/notifications", {});
      if (res?.success) setItems(res.notifications || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Opening the tab marks everything read -> clears the nav dot. Refresh the
    // business so lastReadNotification (and the dot in AdminHome) updates.
    postRequestAuthenticated("/notifications/mark/read", {})
      .then(() => dispatch(fetchbusinessCredentials()))
      .catch(() => {});
  }, [load, dispatch]);

  const isUnread = (n) =>
    lastReadAt ? new Date(n.createdAt) > lastReadAt : true;

  return (
    <div className="w-full px-4 py-7 sm:px-6 lg:px-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-theme-secondaryText">
          Notifications
        </h1>
        <p className="mt-1 text-sm text-theme-placeholder">
          Updates about your plan, billing and community.
        </p>
      </div>

      {loading && items.length === 0 ? (
        <SkeletonList rows={4} />
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-theme-chatDivider bg-theme-secondaryBackground py-14 text-center">
          <FiBell size={26} className="mb-3 text-theme-placeholder" />
          <p className="text-sm font-medium text-theme-secondaryText">
            You're all caught up
          </p>
          <p className="mt-1 text-xs text-theme-placeholder">
            Plan and account updates will show up here.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2.5">
          {items.map((n) => {
            const unread = isUnread(n);
            return (
              <div
                key={n._id}
                className={`relative rounded-2xl border p-4 transition-colors ${
                  unread
                    ? "border-theme-accent/40 bg-theme-accentSoft"
                    : "border-theme-chatDivider bg-theme-secondaryBackground"
                }`}
              >
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-theme-tertiaryBackground">
                    <FiBell size={15} className="text-theme-accent" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      {n.headerText && (
                        <h3 className="truncate text-sm font-semibold text-theme-secondaryText">
                          {n.headerText}
                        </h3>
                      )}
                      <span className="flex-shrink-0 text-[11px] text-theme-placeholder">
                        {fmtTime(n.createdAt)}
                      </span>
                    </div>
                    <p className="mt-1 text-sm leading-relaxed text-theme-primaryText">
                      {n.content}
                    </p>
                    {/* CTA buttons intentionally disabled — notifications are
                        text-only for now. */}
                  </div>
                  {unread && (
                    <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-theme-accent" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NotificationsTab;
